import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Highcharts from 'highcharts';
import { HighchartsReactRefObject } from 'highcharts-react-official';

import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import usePopup from 'hooks/usePopup';
import { ChartContent } from 'components/Chart';
import {
  InsightsChartSpec,
  InsightsChartType,
} from 'services/streamInsightsQuery';

import InlineDialog from '../../InlineDialog';
import buildChartOptions from '../buildChartOptions';
import {
  CHART_TYPE_DESCRIPTIONS,
  CHART_TYPE_LABELS,
} from '../chartOptions/chartTypeMeta';
import { CHART_TYPE_ICONS } from '../chartOptions/chartTypeIcons';

const PREVIEW_HEIGHT = 160;

const NO_LABELS_AXIS = {
  labels: { enabled: false },
  title: { text: undefined },
};

// Hide an axis's tick labels + title without removing the axis itself. Handles
// the single-axis (object) and multi-axis (array) shapes; only ever called for
// an axis that already exists, so it never introduces one on an axis-less chart
// (pie/funnel/…) — shared.ts warns that setting xAxis/yAxis to undefined crashes
// the custom-events plugin.
const hideAxisLabels = <T extends object>(axis: T | T[]): T | T[] =>
  Array.isArray(axis)
    ? axis.map((a) => ({ ...a, ...NO_LABELS_AXIS }))
    : { ...axis, ...NO_LABELS_AXIS };

// A live, non-interactive thumbnail of the current data rendered as `type`.
// buildChartOptions is pure/synchronous, so this needs no refetch — it just
// re-maps the in-memory categories + series. The base options hardcode
// chart.height: 420 (chartOptions/shared.ts), so height MUST be overridden here,
// and all chrome (title/legend/tooltip/hover/axis labels/data labels) is
// stripped for a clean thumbnail.
const buildPreviewOptions = (
  spec: InsightsChartSpec,
  type: InsightsChartType,
): Highcharts.Options => {
  const o = buildChartOptions({ ...spec, chartType: type });
  const po = o.plotOptions ?? {};
  const noDataLabels = { dataLabels: { enabled: false } };

  return {
    ...o,
    chart: { ...o.chart, height: PREVIEW_HEIGHT, width: undefined },
    title: { ...o.title, text: undefined },
    legend: { ...o.legend, enabled: false },
    tooltip: { ...o.tooltip, enabled: false },
    // Only override an axis the source actually set — never add `xAxis: undefined`.
    ...(o.xAxis === undefined ? {} : { xAxis: hideAxisLabels(o.xAxis) }),
    ...(o.yAxis === undefined ? {} : { yAxis: hideAxisLabels(o.yAxis) }),
    plotOptions: {
      ...po,
      series: {
        ...po.series,
        animation: false,
        enableMouseTracking: false,
        dataLabels: { enabled: false },
      },
      // Slice families set their labels per point-type, so disable those too.
      ...(po.pie ? { pie: { ...po.pie, ...noDataLabels } } : {}),
      ...(po.funnel ? { funnel: { ...po.funnel, ...noDataLabels } } : {}),
      ...(po.pyramid ? { pyramid: { ...po.pyramid, ...noDataLabels } } : {}),
      ...(po.packedbubble
        ? { packedbubble: { ...po.packedbubble, ...noDataLabels } }
        : {}),
    },
  };
};

const TriggerButton = styled.div`
  height: 32px;
  padding: 6px 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  border-radius: 10px;
  border: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-primary);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  user-select: none;

  &:hover {
    border-color: var(--color-grey-7);
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--color-text-secondary);
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
`;

const List = styled.div`
  width: 176px;
  padding: 8px;
  max-height: 300px;
  overflow-y: auto;
  border-right: 1px solid var(--color-border-primary);
`;

const Item = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.1s ease;
  background: ${(props) =>
    props.isSelected ? 'var(--color-bg-tertiary)' : 'transparent'};

  &:hover {
    background: var(--color-bg-tertiary);
  }
`;

const PreviewPane = styled.div`
  width: 248px;
  padding: 12px;
  display: flex;
  flex-direction: column;
`;

const PreviewChart = styled.div`
  width: 100%;
  height: ${PREVIEW_HEIGHT}px;
  pointer-events: none;
  margin-bottom: 8px;
`;

const ChartTypeSelect = ({
  spec,
  chartType,
  compatibleTypes,
  onSelect,
}: {
  spec: InsightsChartSpec;
  chartType: InsightsChartType;
  compatibleTypes: InsightsChartType[];
  onSelect: (type: InsightsChartType) => void;
}) => {
  const { isOpen, open, close } = usePopup();
  const previewChartRef = useRef<HighchartsReactRefObject | null>(null);
  // The type whose preview is shown. Falls back to the selected type when the
  // cursor isn't over a row (e.g. right after opening).
  const [hovered, setHovered] = useState<InsightsChartType | null>(null);
  const previewType = hovered ?? chartType;

  const previewOptions = useMemo(
    () => buildPreviewOptions(spec, previewType),
    [spec, previewType],
  );

  const onClose = () => {
    setHovered(null);
    close();
  };

  const SelectedIcon = CHART_TYPE_ICONS[chartType];
  const PreviewIcon = CHART_TYPE_ICONS[previewType];

  return (
    <InlineDialog
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      content={
        <Content>
          <List>
            {compatibleTypes.map((type) => {
              const RowIcon = CHART_TYPE_ICONS[type];
              return (
                <Item
                  key={type}
                  isSelected={type === chartType}
                  data-testid={`select-${type}`}
                  onMouseEnter={() => setHovered(type)}
                  onClick={() => {
                    onSelect(type);
                    onClose();
                  }}
                >
                  <IconSlot>
                    <RowIcon />
                  </IconSlot>
                  <Typography.Body type="Label">
                    {CHART_TYPE_LABELS[type]}
                  </Typography.Body>
                </Item>
              );
            })}
          </List>
          <PreviewPane>
            <PreviewChart>
              <ChartContent
                chartRef={previewChartRef}
                options={previewOptions}
                noDataToDisplay={false}
              />
            </PreviewChart>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 4,
              }}
            >
              <IconSlot>
                <PreviewIcon />
              </IconSlot>
              <Typography.Body type="Label">
                {CHART_TYPE_LABELS[previewType]}
              </Typography.Body>
            </div>
            <Typography.Body type="Body 12" color="var(--color-text-secondary)">
              {CHART_TYPE_DESCRIPTIONS[previewType]}
            </Typography.Body>
          </PreviewPane>
        </Content>
      }
    >
      <TriggerButton
        data-testid="insights-chart-type-select"
        onClick={isOpen ? onClose : open}
      >
        <IconSlot>
          <SelectedIcon />
        </IconSlot>
        <Typography.Body type="Label">
          {CHART_TYPE_LABELS[chartType]}
        </Typography.Body>
        <IconSlot>
          <Icon icon={isOpen ? 'chevron-up' : 'chevron-down'} />
        </IconSlot>
      </TriggerButton>
    </InlineDialog>
  );
};

export default ChartTypeSelect;
